import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, Users, CalendarDays, Check, RotateCcw, RefreshCw } from "lucide-react";

const TYPES = { table: "Bordsbokning", catering: "Catering / Mix Tallrik", takeaway: "Större avhämtning", question: "Övrig fråga" };
const fmt = (iso) => new Date(iso).toLocaleString("sv-SE", { timeZone: "Europe/Stockholm", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

export const Inbox = ({ http, onCount }) => {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("open");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await http.get("/admin/contacts");
      const items = Array.isArray(r.data) ? r.data : [];
      setList(items);
      onCount?.(items.filter((c) => !c.handled).length);
    } catch {
      toast.error("Kunde inte hämta förfrågningar");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (c) => {
    try {
      await http.patch(`/admin/contacts/${c.id}`, { handled: !c.handled });
      load();
    } catch {
      toast.error("Något gick fel");
    }
  };

  const shown = list.filter((c) => (filter === "open" ? !c.handled : filter === "done" ? c.handled : true));
  const openCount = list.filter((c) => !c.handled).length;

  return (
    <div data-testid="admin-inbox" className="mx-auto mt-10 max-w-2xl space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h1 className="font-display text-4xl">Förfrågningar</h1>
        <button data-testid="inbox-refresh-button" onClick={load} className="flex items-center gap-2 text-sm text-[#721121]"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Uppdatera</button>
      </div>
      <div className="flex gap-2">
        {[["open", `Nya (${openCount})`], ["done", "Hanterade"], ["all", "Alla"]].map(([k, l]) => (
          <button key={k} data-testid={`inbox-filter-${k}`} onClick={() => setFilter(k)} className={`rounded-full px-4 py-2 text-sm transition-colors duration-300 ${filter === k ? "bg-[#721121] text-white" : "bg-white text-[#1C1917] ring-1 ring-[#721121]/15"}`}>{l}</button>
        ))}
      </div>
      <ul data-testid="inbox-list" className="space-y-3">
        {!loading && shown.length === 0 && <li className="rounded-[20px] bg-white p-6 text-sm text-[#57534E] ring-1 ring-[#721121]/10">Inga förfrågningar här.</li>}
        {shown.map((c) => (
          <li key={c.id} data-testid={`inbox-item-${c.id}`} className={`rounded-[20px] bg-white p-5 ring-1 ring-[#721121]/10 ${c.handled ? "opacity-70" : ""}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#721121]">{TYPES[c.inquiry_type] || c.inquiry_type} · {fmt(c.created_at)}</p>
                <p className="font-display mt-1 text-2xl">{c.name}</p>
              </div>
              <button data-testid={`inbox-toggle-${c.id}`} onClick={() => toggle(c)} className={`flex shrink-0 items-center gap-1.5 rounded-full px-3 py-2 text-xs font-medium transition-colors duration-300 ${c.handled ? "bg-[#FAF7F2] text-[#57534E] ring-1 ring-[#721121]/15" : "bg-[#721121] text-white"}`}>
                {c.handled ? <><RotateCcw size={13} /> Ångra</> : <><Check size={13} /> Klar</>}
              </button>
            </div>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-[#1C1917]">
              <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 text-[#721121]"><Phone size={14} /> {c.phone}</a>
              {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 text-[#721121]"><Mail size={14} /> {c.email}</a>}
              {c.guests && <span className="flex items-center gap-1.5"><Users size={14} /> {c.guests}</span>}
              {c.date && <span className="flex items-center gap-1.5"><CalendarDays size={14} /> {c.date}</span>}
            </div>
            {c.message && <p className="mt-3 whitespace-pre-wrap rounded-xl bg-[#FAF7F2] p-3 text-sm text-[#57534E]">{c.message}</p>}
          </li>
        ))}
      </ul>
    </div>
  );
};
